// Rapport composé « Ajouter au rapport » (DOC-17) : panier d'éléments par conversation,
// enregistré côté serveur avec la conversation (BE-09, useReportBasketSync.js).
// Module pur (aucun import React), testé par node --test.
//
// Un élément du panier ne copie PAS la spécification : il référence le visuel du
// message ({message_id, artifact_id}) et garde de quoi s'afficher dans la liste.
// À la génération, la spécification validée par le backend est relue dans les
// messages de la conversation : le rapport contient exactement les données affichées
// (critère 2 de DOC-17). Un visuel retiré de la conversation (réponse régénérée) est
// signalé « indisponible » et n'est pas envoyé.
//
// Élément : {key, message_id, artifact_id, kind, visual_type, view_type, title, added_at}

// Même plafond que le serveur (COMPOSE_MAX_ARTIFACTS, visuels d'un document) : un panier
// accepté ici n'est jamais refusé à la génération
export const BASKET_MAX = 8;
export const BASKET_PREFIX = 'digitrans_report_basket:';
export const REPORT_TITLE_MAX = 120;

// Familles composables : graphiques, indicateurs, tableaux, cartes, plannings.
// Une page web (embed) n'a pas de données ; un document est déjà un fichier.
const REPORTABLE_KINDS = new Set(['chart', 'kpi', 'table', 'map', 'planning']);

export const basketStorageKey = (conversationId) => `${BASKET_PREFIX}${conversationId}`;
export const itemKey = (messageId, artifactId) => `${messageId}::${artifactId}`;

/** Le visuel peut-il rejoindre le rapport ? (prêt, spécification validée, famille de données) */
export function isReportable(artifact) {
  if (!artifact || artifact.status !== 'ready' || !artifact.spec || typeof artifact.spec !== 'object') return false;
  const kind = artifact.spec.kind ?? artifact.kind;
  return REPORTABLE_KINDS.has(kind) && artifact.spec.visual_type !== 'embed';
}

const text = (v, max = 200) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null);

/** Élément du panier à partir d'un visuel de message. */
export function makeItem({ messageId, artifactId, spec, viewType = null, now = Date.now() }) {
  if (messageId == null || artifactId == null) return null;
  return {
    key: itemKey(messageId, artifactId),
    message_id: String(messageId),
    artifact_id: String(artifactId),
    kind: text(spec?.kind, 20),
    visual_type: text(spec?.visual_type, 40),
    view_type: text(viewType, 40),
    title: text(spec?.title) ?? 'Visuel sans titre',
    added_at: now,
  };
}

/** Liste relue du stockage local : seuls les éléments bien formés sont gardés. */
export function sanitizeItems(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const item = makeItem({
      messageId: typeof raw.message_id === 'string' ? raw.message_id : null,
      artifactId: typeof raw.artifact_id === 'string' ? raw.artifact_id : null,
      spec: { kind: raw.kind, visual_type: raw.visual_type, title: raw.title },
      viewType: raw.view_type,
      now: typeof raw.added_at === 'number' ? raw.added_at : 0,
    });
    if (!item || seen.has(item.key)) continue;
    seen.add(item.key);
    out.push(item);
    if (out.length >= BASKET_MAX) break;
  }
  return out;
}

/**
 * Éléments tels qu'enregistrés côté serveur (PUT /api/conversations/:id/basket, BE-09) :
 * sans la clé d'affichage, champs dans l'ordre du stockage.
 */
export function toStoredItems(items) {
  return items.map(({ message_id, artifact_id, kind, visual_type, view_type, title, added_at }) => ({
    message_id, artifact_id, kind, visual_type, view_type, title, added_at,
  }));
}

/** Deux paniers identiques (mêmes éléments, même ordre, mêmes champs enregistrés) ? */
export function sameBasket(a, b) {
  return JSON.stringify(toStoredItems(a)) === JSON.stringify(toStoredItems(b));
}

/** Fusion : `primary` d'abord, puis les éléments de `extra` absents, BASKET_MAX au plus. */
export function mergeBaskets(primary, extra) {
  const out = primary.slice(0, BASKET_MAX);
  const keys = new Set(out.map((i) => i.key));
  for (const item of extra) {
    if (out.length >= BASKET_MAX) break;
    if (keys.has(item.key)) continue;
    keys.add(item.key);
    out.push(item);
  }
  return out;
}

/** Réducteur du panier : add, remove, move, clear, replace. */
export function basketReducer(items, action) {
  switch (action.type) {
    case 'add': {
      if (!action.item || items.some((i) => i.key === action.item.key) || items.length >= BASKET_MAX) return items;
      return [...items, action.item];
    }
    case 'remove':
      return items.filter((i) => i.key !== action.key);
    case 'toggle': {
      if (!action.item) return items;
      return items.some((i) => i.key === action.item.key)
        ? items.filter((i) => i.key !== action.item.key)
        : basketReducer(items, { type: 'add', item: action.item });
    }
    case 'move': {
      const from = items.findIndex((i) => i.key === action.key);
      if (from === -1) return items;
      const to = Math.max(0, Math.min(items.length - 1, action.to));
      if (to === from) return items;
      const next = [...items];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    }
    case 'clear':
      return items.length ? [] : items;
    case 'replace':
      return sanitizeItems(action.items);
    default:
      return items;
  }
}
